import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button } from "baseui/button";
import { Stepper } from "baseui/stepper";
import { HeadingSmall, HeadingXSmall } from "baseui/typography";
import { addDays, format, startOfDay, setHours, setMinutes, setSeconds, setMilliseconds } from "date-fns";
import { useEffect, useState } from "react";
import { Chip } from "../../../components/Chip";
import { Flex } from "../../../components/Flex";
import { Bold } from "../../../components/Bold";
import { BaseDatePicker } from "../../../components/BaseDatePicker";
import { Notice } from "../../../components/ui/Notice";
import { es as esLocale } from "date-fns/locale";
import { useT } from "../../../i18n/admin";

export type DurationOption = {
  mode: "one" | "multiple";
  date: string | undefined;
  startDate?: string;
  endDate?: string;
  confirmed?: boolean;
  adults?: number;
  children?: number;
};

interface Props {
  onChange?: (duration: DurationOption) => void;
  defaultValue?: DurationOption;
  isLoading?: boolean;
  accessType?: string;
  // Per-invitation-type guest limits (SAM-154). Undefined means "unlimited"
  // (the backend left the field blank).
  maxAdults?: number;
  maxChildren?: number;
  maxGuestsPerDay?: number;
  // SAM-199 follow-up — a SERVICE ('day' type) booking cannot span a date
  // range today: SAM's create_booking always reduces the reservation to a
  // single calendar date (see functions/booking/lambda_function.py and
  // day_schedule.py in sam-lambda-functions, and serviceDateReadiness.ts's
  // header for the full trail). AccessForm sets this while the operator is
  // booking a SERVICE so "Multiple days" is never offered as a dead end.
  restrictToOneDay?: boolean;
  restrictionReason?: string;
  /**
   * SAM-1234: this caller or pass type cannot give a pass of several days (multiDayRule.ts), so «Multiple days» is
   * not shown at all, instead of a disabled option with a reason.
   */
  hideMultipleDays?: boolean;
}

export const HowLongControl = ({ onChange, defaultValue, isLoading = false, accessType, maxAdults, maxChildren, maxGuestsPerDay, restrictToOneDay = false, restrictionReason, hideMultipleDays = false }: Props) => {
  const [mode, setMode] = useState<"one" | "multiple">(
    defaultValue?.mode || "one"
  );
  const today = new Date();
  
  // Default to today's date if no date is provided and mode is "one"
  const defaultDate = defaultValue?.date || (
    defaultValue?.mode === "one" || !defaultValue?.mode 
      ? setMilliseconds(setSeconds(setMinutes(setHours(startOfDay(today), 0), 0), 0), 0).toISOString()
      : undefined
  );
  
  const [value, setValue] = useState<string | undefined>(defaultDate);
  const [dateRange, setDateRange] = useState<Date[]>(() => {
    if (defaultValue?.startDate && defaultValue?.endDate) {
      return [new Date(defaultValue.startDate), new Date(defaultValue.endDate)];
    } else if (defaultValue?.startDate) {
      return [new Date(defaultValue.startDate)];
    }
    return [];
  });
  const [isConfirmed, setIsConfirmed] = useState(defaultValue?.confirmed || false);
  const [adults, setAdults] = useState(defaultValue?.adults || 1);
  const [children, setChildren] = useState(defaultValue?.children || 0);
  const [css, theme] = useStyletron();
  const { t, lang } = useT();
  const dateLocale = lang === "es" ? { locale: esLocale } : undefined;

  const next7Days = Array.from({ length: 7 }, (_, i) => {
    const date = addDays(today, i);
    // Set time to start of day and format as ISO string
    const dateWithTime = setMilliseconds(setSeconds(setMinutes(setHours(startOfDay(date), 0), 0), 0), 0);
    return {
      label: format(date, "eee", dateLocale),
      value: dateWithTime.toISOString(),
      fullLabel: format(date, "d MMM", dateLocale),
      index: i,
    };
  });

  useEffect(() => {
    if (!mode) return;
    
    if (mode === "one" && value && isConfirmed) {
      onChange?.({ mode, date: value, confirmed: true, adults, children });
    } else if (mode === "multiple" && dateRange.length === 2 && isConfirmed) {
      onChange?.({ 
        mode, 
        date: undefined,
        startDate: dateRange[0].toISOString(),
        endDate: dateRange[1].toISOString(),
        confirmed: true,
        adults,
        children
      });
    }
  }, [value, mode, dateRange, isConfirmed, adults, children, onChange]);

  // Guest limits (SAM-154). Only treat a positive finite number as an actual
  // cap; null/undefined/0 from the backend means "unlimited".
  const hasAdultCap = typeof maxAdults === "number" && Number.isFinite(maxAdults) && maxAdults > 0;
  const hasChildCap = typeof maxChildren === "number" && Number.isFinite(maxChildren) && maxChildren >= 0;
  const hasTotalCap = typeof maxGuestsPerDay === "number" && Number.isFinite(maxGuestsPerDay) && maxGuestsPerDay > 0;

  const limitError = (() => {
    if (hasAdultCap && adults > (maxAdults as number)) {
      return t(maxAdults === 1 ? "booking.howLong.maxAdultsOne" : "booking.howLong.maxAdultsMany", { n: maxAdults as number });
    }
    if (hasChildCap && children > (maxChildren as number)) {
      return t(maxChildren === 1 ? "booking.howLong.maxChildrenOne" : "booking.howLong.maxChildrenMany", { n: maxChildren as number });
    }
    if (hasTotalCap && adults + children > (maxGuestsPerDay as number)) {
      return t("booking.howLong.maxPerDay", { n: maxGuestsPerDay as number });
    }
    return "";
  })();

  const handleGuestConfirm = () => {
    if (limitError) return;
    setIsConfirmed(true);
  };

  const handleEdit = () => {
    setIsConfirmed(false);
  };

  const handleAdultChange = (newValue: number) => {
    const clamped = hasAdultCap ? Math.min(newValue, maxAdults as number) : newValue;
    setAdults(Math.max(1, clamped));
  };

  const handleChildrenChange = (newValue: number) => {
    const clamped = hasChildCap ? Math.min(newValue, maxChildren as number) : newValue;
    setChildren(Math.max(0, clamped));
  };

  const canProceed = (mode === "one" && value) || (mode === "multiple" && dateRange.length === 2);

  // Check if access type is "Delivery" - if so, disable multiple days option
  const isDelivery = accessType?.toLowerCase() === "delivery";

  // SAM-199 follow-up — a SERVICE booking cannot use a date range (see the
  // `restrictToOneDay` prop doc above). If the operator was already in
  // "multiple" mode (e.g. they had it selected while booking a PRODUCT and
  // then switched to Service), snap back to "one" so the rest of this
  // control — and AccessForm's readiness check — stay consistent instead of
  // silently keeping a range the SERVICE path can never use.
  useEffect(() => {
    if ((restrictToOneDay || hideMultipleDays) && mode === "multiple") {
      setMode("one");
      setIsConfirmed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictToOneDay, hideMultipleDays]);

  const multipleDaysDisabled = isDelivery || restrictToOneDay;

  return (
    <Block marginTop="scale500">
      <Flex display="flex" gap="10px" marginBottom={"40px"}>
        <Chip
          $size="large"
          $selected={mode === "one"}
          onClick={() => {
            setMode("one");
            setIsConfirmed(false);
          }}
        >
          {t("booking.howLong.oneDay")}
        </Chip>
        {!hideMultipleDays && (
        <Chip
          $size="large"
          $selected={mode === "multiple"}
          onClick={() => {
            if (!multipleDaysDisabled) {
              setMode("multiple");
              setIsConfirmed(false);
            }
          }}
          disabled={multipleDaysDisabled}
          style={{
            opacity: multipleDaysDisabled ? 0.5 : 1,
            cursor: multipleDaysDisabled ? 'not-allowed' : 'pointer'
          }}
        >
          {t("booking.howLong.multipleDays")}
        </Chip>
        )}
      </Flex>

      {restrictToOneDay && (
        <Block marginTop="-16px" marginBottom="24px">
          <Notice variant="info">
            {restrictionReason || t("booking.howLong.singleDayOnly")}
          </Notice>
        </Block>
      )}

      {!isConfirmed && mode === "one" && (
        <Block
          display="grid"
          gridTemplateColumns="repeat(3, 1fr)"
          gridGap="scale600"
        >
          {next7Days.map((day, i) => {
            const selected = value === day.value; // Added selected logic
            return (
              <Block
                key={day.value}
                onClick={() => setValue(day.value)}
                className={css({
                  cursor: "pointer",
                  border: selected
                    ? `3px solid var(--tenant-primary, ${theme.colors.primary})`
                    : `1px solid ${theme.colors.borderOpaque}`,
                  borderRadius: theme.borders.radius400,
                  padding: theme.sizing.scale700,
                  textAlign: "center",
                  backgroundColor: theme.colors.backgroundSecondary,
                })}
              >
                <Block
                  font={selected ? "font450" : "font400"}
                  color={selected ? "primary" : "contentPrimary"}
                >
                  {i === 0 ? t("booking.howLong.today") : day.label}
                </Block>
                <Block font="font250" color="contentSecondary">
                  {day.fullLabel}
                </Block>
              </Block>
            );
          })}
        </Block>
      )}

      {!isConfirmed && mode === "multiple" && (
        <Block marginTop="16px">
          <Block marginBottom="12px" font="font300" color="contentPrimary">
            {t("booking.howLong.selectRange")}
          </Block>
          <BaseDatePicker
            mode="range"
            rangeValue={{
              from: dateRange[0] || undefined,
              to: dateRange[1] || undefined,
            }}
            onRangeChange={(range) => {
              if (range?.from && range?.to) {
                setDateRange([range.from, range.to]);
              } else if (range?.from) {
                setDateRange([range.from]);
              } else {
                setDateRange([]);
              }
            }}
            minDate={new Date()}
            placeholder={t("booking.howLong.rangePlaceholder")}
          />
        </Block>
      )}

      {isConfirmed && mode === "multiple" && dateRange.length === 2 && (
        <Block marginTop="16px">
          <Block
            padding="16px"
            className={css({
              border: `2px solid var(--tenant-primary, ${theme.colors.primary})`,
              borderRadius: '8px',
              backgroundColor: theme.colors.backgroundSecondary,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            })}
          >
            <Block>
              <Block font="font300" color="contentSecondary" marginBottom="4px">
                {t("booking.howLong.selectedRange")}
              </Block>
              <Block font="font450" color="primary">
                {format(dateRange[0], "MMM dd, yyyy", dateLocale)} - {format(dateRange[1], "MMM dd, yyyy", dateLocale)}
              </Block>
            </Block>
            <Button
              size="compact"
              kind="secondary"
              onClick={handleEdit}
              overrides={{
                BaseButton: {
                  style: {
                    borderRadius: '6px',
                  }
                }
              }}
            >
              {t("common.edit")}
            </Button>
          </Block>
        </Block>
      )}

      {/* Guest Selection */}
      {!isConfirmed && canProceed && (
        <Block marginTop="2rem">
          <Block 
            marginBottom="1.5rem" 
            font="font400"
            color="contentPrimary"
            $style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#000'
            }}
          >
            {t("booking.howLong.howMany")}
          </Block>
          <Flex
            flexDirection="column"
            gap="1rem"
            maxWidth="400px"
            padding="10px 16px"
            paddingRight="40px"
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Flex flexDirection="column" gap="0.25rem">
                <Bold>{t("booking.howLong.adults")}</Bold>
                <Block font="font200" color="contentSecondary">
                  {t("booking.howLong.adultsHint")}{hasAdultCap ? ` · ${t("booking.unit.max", { max: maxAdults as number })}` : ""}
                </Block>
              </Flex>
              <Stepper
                value={adults}
                setValue={handleAdultChange}
                minValue={1}
                maxValue={hasAdultCap ? (maxAdults as number) : undefined}
              />
            </Flex>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex flexDirection="column" gap="0.25rem">
                <Bold>{t("booking.howLong.children")}</Bold>
                <Block font="font200" color="contentSecondary">
                  {t("booking.howLong.childrenHint")}{hasChildCap ? ` · ${t("booking.unit.max", { max: maxChildren as number })}` : ""}
                </Block>
              </Flex>
              <Stepper
                value={children}
                setValue={handleChildrenChange}
                minValue={0}
                maxValue={hasChildCap ? (maxChildren as number) : undefined}
              />
            </Flex>
            <Flex justifyContent="space-between" alignItems="center" fontWeight="bold">
              <HeadingSmall>{t("booking.howLong.total")}</HeadingSmall>
              <HeadingXSmall>
                {t(adults + children === 1 ? "booking.guests.one" : "booking.guests.many", { n: adults + children })}
                {hasTotalCap ? t("booking.howLong.ofMax", { n: maxGuestsPerDay as number }) : ""}
              </HeadingXSmall>
            </Flex>
            {limitError && <Notice variant="error">{limitError}</Notice>}
          </Flex>
          <Block marginTop="32px" marginBottom="24px">
            <Button
              onClick={handleGuestConfirm}
              size="default"
              kind="primary"
              disabled={isLoading || Boolean(limitError)}
              isLoading={isLoading}
              overrides={{
                BaseButton: {
                  style: {
                    width: '100%',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    padding: '16px 24px',
                    marginBottom: '16px',
                    backgroundColor: 'var(--tenant-primary, #111827)',
                    ':hover': {
                      backgroundColor: 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)',
                    },
                  }
                }
              }}
            >
              {t("common.next")}
            </Button>
          </Block>
        </Block>
      )}


    </Block>
  );
};
